<template>
  <BaseCard
    v-bind="$attrs"
    :url-link="apartmentUrl(apartment)"
    :url-image="image"
    :image-floorplan="isFloorplanImage"
    :campaign-highlight="campaignHighlight"
    :badge="{text: translate(apartmentDisplayType(apartment)), color: apartmentDisplayColor(apartment)}"
    :hover-text="translate('Tutustu asuntoon')"
    :means-test="meansTest"
    :income-limit="incomeLimit"
    :own-plot="ownPlot"
    :housing-type="housingType"
    class="apartment-card"
    :class="{
      'apartment-card--has-showing': apartmentHasUpcomingShowing(apartment),
      'apartment-card--owned': apartment.type === 'owner-occupancy',
    }"
  >
    <template v-slot:default="{title, line}">
      <div class="apartment-card__header">
        <div :class="line">
          {{apartmentDisplayLocation(apartment)}}
        </div>

        <h2 :class="title">
          {{apartment.mpc_name}}
        </h2>
      </div>
    </template>

    <!-- Seuraava esittely -->
    <template #afterImage>
      <div
        v-if="apartmentHasUpcomingShowing(apartment)"
        class="apartment-card__presentations"
      >
        <div class="apartment-card__next-showing">
          <InlineSvg :svg="calendarIcon" />
          <span>
            {{sprintf(translate('Esittely %s'), apartmentDisplayShowing(apartment))}}
          </span>

          <button
            v-if="additionalPresentations.length"
            type="button"
            class="apartment-card__presentations-toggle"
            :aria-expanded="presentationExpanded.toString()"
            @click.stop.prevent="togglePresentations"
          >
            <span v-if="!presentationExpanded">
              + {{additionalPresentations.length}} {{translate('lisää')}}
            </span>
            <span v-else>{{translate('Piilota')}}</span>
          </button>
        </div>

        <div
          v-if="presentationExpanded"
          class="apartment-card__presentations-more"
        >
          <div class="apartment-card__presentations-more-title">
            {{translate('Seuraavat esittelyajat')}}
          </div>
          <div
            v-for="presentation in additionalPresentations"
            :key="presentation.dg_asuntoesittelyaikaid"
            class="apartment-card__presentation-time"
          >
            {{apartmentDisplayPresentation(presentation)}}
          </div>
        </div>
      </div>
    </template>

    <!-- Basic specs -->
    <template #apartmentSpecs>
      <div class="base-card__specs base-card__specs--row">
        <span class="base-card__spec apartment-type">
          {{apartment.mpc_huoneistotyyppi}}
        </span>
        <span class="base-card__separator">|</span>

        <span class="base-card__spec area">
          {{apartmentDisplayArea(apartment)}}
        </span>
        <span class="base-card__separator">|</span>

        <span class="base-card__spec building-type">
          {{ apartment.building.mpc_talotyyppi ? translate(apartment.building.mpc_talotyyppi) : ''}}
        </span>

        <template v-if="floorInfo">
          <span class="base-card__separator">|</span>
          <span class="base-card__spec floor-info">
            {{ floorInfo }}
          </span>
        </template>
      </div>
    </template>

    <template #footer>

      <!-- Action buttons -->
      <div class="apartment-card__footer-attribute">
        <div v-if="isReserved">
          <div class="apartment-card__footer-attribute-title">&nbsp;</div>
          <div class="apartment-card__footer-attribute-value">{{translate('Varattu')}}</div>
        </div>
        <div v-else>
          <div class="apartment-card__footer-attribute-title">{{translate('Vapaa')}}</div>
          <div class="apartment-card__footer-attribute-value">{{apartmentDisplayAvailable(apartment)}}</div>
        </div>
      </div>

      <div
          v-if="apartment.type === 'owner-occupancy'"
          class="apartment-card__footer-attribute">
            <div class="apartment-card__footer-attribute-title">{{ translate('Myyntihinta') }}</div>
            <div class="apartment-card__footer-attribute-value">{{ apartmentDisplaySellPrice(apartment) }}</div>
        </div>

      <div
        v-if="apartment.type === 'owner-occupancy'"
        class="apartment-card__footer-attribute">
          <div class="apartment-card__footer-attribute-title">{{ translate(apartmentDisplayPriceTitle(apartment)) }}</div>
          <div class="apartment-card__footer-attribute-value">{{ apartmentDisplayPrice(apartment) }}</div>
      </div>
      <div
        v-else
        class="apartment-card__footer-attribute">
        <div class="apartment-card__footer-attribute-title">{{translate(apartmentDisplayPriceTitle(apartment))}}</div>
        <div class="apartment-card__footer-attribute-value">{{apartmentDisplayPrice(apartment)}}</div>
      </div>

      <div
        v-if="apartment.type === 'right-of-occupancy'"
        class="apartment-card__footer-attribute"
      >
        <div class="apartment-card__footer-attribute-title">{{translate('ASO-maksu')}}</div>
        <div class="apartment-card__footer-attribute-value">
          {{`${formatStringDecimal(apartment.mpc_asomaksuyhteensa)} €`}}
        </div>
      </div>
    </template>

    <template #afterLink v-if="!hideButtons">
      <div class="apartment-card__favourite" :test="apartment.mpc_status">
        <FavouriteToggle
          :uuid="apartment.mpc_asuntoid"
          :type="apartment.type ? apartment.type : 'right-of-occupancy'"
          :status="apartment.mpc_status"
        />
      </div>

      <button
        v-if="apartment.type === 'owner-occupancy'"
        class="apartment-card__contact-link btn btn--green"
        type="button"
        data-modal-toggle="contact-form"
        :data-contact-form-details="contactFormDetails"
      >
        {{translate('Jätä yhteydenottopyyntö')}}
      </button>
    </template>

  </BaseCard>
</template>

<script>
  import {sprintf} from 'sprintf-js';

  import {formatStringDecimal, formatStringInt} from '../../lib/format';
  import utils from '../mixins/utils';
  import apartmentDisplayFields from '../mixins/apartmentDisplayFields';
  import calendarIcon from '../../../icons/calendar.svg?raw';
  import FavouriteToggle from '../FavouriteToggle';
  import BaseCard from './BaseCard';

  export default {
    components: {
      FavouriteToggle,
      BaseCard,
    },
    mixins: [
      apartmentDisplayFields,
      utils.translate,
      utils.apartmentUrl,
    ],
    props: {
      apartment: {
        type: Object,
        required: true,
      },
      showFloorplan: {
        type: Boolean,
        required: false,
        default: false,
      },
      hideButtons: {
        type: Boolean,
        required: false,
        default: false,
      },
    },
    computed: {
      presentations() {
        return Array.isArray(this.apartment?.presentations)
          ? this.apartment.presentations
          : [];
      },
      additionalPresentations() {
        return this.presentations.slice(1);
      },
      floorInfo() {
        return this.apartmentDisplayFloorInfo(this.apartment, true);
      },
      image() {
        if (this.apartment.imagesForWeb.length <= 0 ) {
          return null;
        }
        let floorPlan = null;
        let mainPicture = null;

        for (let i = 0; i < this.apartment.imagesForWeb.length; i++) {
          if (this.apartment.imagesForWeb[i].type === 'Pohjakuva') {
            floorPlan = this.apartment.imagesForWeb[i];
          }
          if (!mainPicture && this.apartment.imagesForWeb[i].type !== 'Myyntiasiakirja') {
            mainPicture = this.apartment.imagesForWeb[i];
          }
        }

        // Choose which image to display floorPlan OR mainPicture.
        if (this.showFloorplan) {
          return floorPlan ? floorPlan.storage_thumbnail : null;
        } else {
          return mainPicture ? mainPicture.storage_thumbnail : null;
        }
      },
      isFloorplanImage() {
        return this.showFloorplan && !!this.image;
      },
      campaignHighlight() {
        const text = this.apartment?.current_campaign?.dg_asuntokampanjankarkiteksti;
        if (typeof text !== 'string') {
          return null;
        }
        const trimmed = text.trim();
        return trimmed !== '' ? trimmed : null;
      },
      isReserved() {
        return this.apartment.mpc_status === 'Varattu';
      },
      incomeLimit() {
        if (this.apartment.type != 'owner-occupancy') {
          return this.apartment.housing_locality.new_tulorajat;
        } else {
          return false;
        }
      },
      meansTest() {
        return this.apartment.housing_locality.ava_tarveharkinta;
      },
      ownPlot() {
        return this.apartment.housing_locality.ava_tontinomistus === "Oma" ? true : false;
      },
      housingType() {
        return this.apartment.type;
      },
      contactFormDetails() {
        const location = [
          this.apartment.city?.mpc_name,
          this.apartment.township?.name,
        ].filter(Boolean).join(', ');
        const specs = [
          this.apartment.mpc_huoneistotyyppi,
          this.apartmentDisplayArea(this.apartment),
          this.apartment.building?.mpc_talotyyppi,
        ].filter(Boolean).join(' | ');

        return [location, this.apartment.mpc_katuosoite, specs]
          .filter(Boolean)
          .join('\n');
      },
    },
    methods: {
      sprintf,
      formatStringDecimal,
      togglePresentations() {
        this.presentationExpanded = !this.presentationExpanded;
      },
    },
    data() {
      return {
        presentationExpanded: false,
        calendarIcon,
      };
    }
  }
</script>

<style lang="scss">
  @import "~styles/definitions";

  // Image + showing stripe share a fixed height, so texts below start at the same
  // level whether or not the card has a showing stripe.
  $apartment-card-image-height: r(384);
  $apartment-card-showing-height: r(50);

  .apartment-card {
    display: flex;
    flex-flow: column;
    width: 100%;

    .base-card__image {
      height: calc(#{$apartment-card-image-height} + #{$apartment-card-showing-height});
    }

    &--has-showing .base-card__image {
      height: $apartment-card-image-height;
    }

    // 1rem between the specs row and the availability/price row.
    .base-card__specs {
      padding-bottom: 1rem;
    }

    // 0.5rem between the "Add to application" and "Contact" buttons.
    &--owned .favourite-toggle__wrapper {
      padding-bottom: 0;
    }
  }

  .apartment-card__header {
    color: $black;
  }

  .apartment-card__footer-attribute {
    margin-right: r(20);

    &:last-child {
      margin-right: 0;
    }
  }

  .apartment-card__footer-attribute-title {
    font-size: r(12);
    text-transform: uppercase;
    line-height: r(16);
  }

  .apartment-card__footer-attribute-value {
    font-size: 1rem;
    font-weight: $font-weight-bold;
  }

  .apartment-card__next-showing {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    box-sizing: border-box;
    height: $apartment-card-showing-height;
    padding: 0 1rem;
    background-color: $color-pink-light-2025;
    font-weight: $font-weight-bold;
    font-size: 1rem;
    white-space: nowrap;
    color: $color-green-2025;

    .icon {
      flex: 0 0 auto;
      margin-right: .75rem;
    }

    > span {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .apartment-card__presentations {
    position: relative;
    background-color: $color-pink-light-2025;
    color: $color-green-2025;
    font-weight: $font-weight-bold;
  }

  .apartment-card__presentations-toggle {
    flex: 0 0 auto;
    margin-left: auto;
    border: 0;
    border-radius: 999px;
    padding: .35rem .7rem;
    background-color: $color-pink-2025;
    color: $color-green-2025;
    font: inherit;
    font-size: .8rem;
    line-height: 1.1;
    white-space: nowrap;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      background-color: $color-white;
    }
  }

  .apartment-card__presentations-more {
    position: absolute;
    z-index: 4;
    top: 100%;
    right: 0;
    left: 0;
    padding: .5rem 1rem .85rem 3.25rem;
    background-color: $color-pink-light-2025;
    box-shadow: 0 .25rem .5rem rgba($color-black, .15);
    font-size: .9rem;
  }

  .apartment-card__presentations-more-title {
    display: flex;
    align-items: center;
    gap: .5rem;
    margin-bottom: .35rem;

    .icon {
      flex: 0 0 auto;
    }
  }

  .apartment-card__presentation-time {
    padding-top: .2rem;
  }

  .apartment-card__favourite {
    width: 100%;
  }

  .apartment-card__contact-link {
    display: inline-flex;
    font-weight: 500;
    margin-top: $theme-spacing-0-5;
    width: 100%;

    &::before {
      display: inline-block;
      width: r(20);
      height: r(20);
      background-color: currentColor;
      content: '';
      mask: url('../../../icons/message-circle.svg') center / contain no-repeat;
      -webkit-mask: url('../../../icons/message-circle.svg') center / contain no-repeat;
    }
  }
</style>
